import { Button, HStack } from "@chakra-ui/react";
import { formatISO, parseISO } from "date-fns";
import { AuditingDrawerProps } from "features/auditing/AuditingPanel";
import { AuditLogTableFilterData } from "gen/model/auditLogTableFilterData";
import { AuditLogTableQueryDataRequest } from "gen/model/auditLogTableQueryDataRequest";
import { BooleanTableCellValue } from "gen/model/booleanTableCellValue";
import { GenericTableData } from "gen/model/genericTableData";
import { isEmpty, noop } from "lodash-es";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DateRangeButton } from "shared/components/input/date/DateRangeButton";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { selectOptionsFromGenericSelectData } from "shared/components/input/select/SelectUtils";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { StringParam, useQueryParam } from "use-query-params";

export const formatDateParam = (
  date: Date | null | undefined,
): string | undefined =>
  date ? formatISO(date, { representation: "date" }) : undefined;

export const parseDateParam = (
  date: string | null | undefined,
): Date | undefined => (date ? parseISO(date) : undefined);

export const AuditingTable: React.FC<{
  tableData: GenericTableData | undefined;
  auditLogFilterData: AuditLogTableFilterData | undefined;
  renderAuditingPanel: (props: AuditingDrawerProps) => React.ReactNode;
  filters: AuditLogTableQueryDataRequest;
  setFilters: (filters: AuditLogTableQueryDataRequest) => void;
}> = ({
  tableData,
  auditLogFilterData,
  renderAuditingPanel,
  filters,
  setFilters,
}) => {
  const { t } = useTranslation(["common", "auditing", "enums"]);

  const [logEntryId, setLogEntryId] = useQueryParam("id", StringParam);

  const hasFilters = Object.values(filters).some((value) => !isEmpty(value));

  const clearFilters = () =>
    setFilters({
      start_date: null,
      end_date: null,
      user_ids: null,
      operation_ids: null,
      audit_action_types: null,
      fund_family_ids: null,
    });

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const hasModelAuditLogEntries =
        (
          row.data?.cells["has_model_log_entries"]
            .value as BooleanTableCellValue
        ).value || false;
      if (!hasModelAuditLogEntries) {
        return undefined;
      }
      return {
        type: "onclick",
        onClick: () => setLogEntryId(row.id),
      };
    },
    [setLogEntryId],
  );

  return (
    <TableBox>
      <TableBoxHeader>
        <HStack
          flexBasis="min-content"
          alignItems="center"
          justifyContent="flex-start"
        >
          <DateRangeButton
            value={{
              start: parseDateParam(filters.start_date),
              end: parseDateParam(filters.end_date),
            }}
            onChangeValue={({ start, end }) => {
              setFilters({
                ...filters,
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
              });
            }}
          />
          {auditLogFilterData?.users.length && (
            <SelectButton
              isMulti={true}
              title={t("common:user")}
              size="sm"
              isFilterButton={true}
              value={filters.user_ids}
              onChangeValue={(user_ids) => setFilters({ ...filters, user_ids })}
            >
              {selectOptionsFromGenericSelectData(auditLogFilterData.users)}
            </SelectButton>
          )}
          <SelectButton
            title={t("common:action")}
            size="sm"
            isMulti={true}
            isFilterButton={true}
            value={filters.audit_action_types}
            onChangeValue={(audit_action_types) =>
              setFilters({ ...filters, audit_action_types })
            }
          >
            {selectOptionsFromGenericSelectData(
              auditLogFilterData?.audit_actions,
            )}
          </SelectButton>
          {auditLogFilterData?.operations.length && (
            <SelectButton
              title={t("auditing:objectEvent")}
              size="sm"
              isMulti={true}
              isFilterButton={true}
              value={filters.operation_ids}
              onChangeValue={(operation_ids) =>
                setFilters({ ...filters, operation_ids })
              }
            >
              {selectOptionsFromGenericSelectData(
                auditLogFilterData.operations,
              )}
            </SelectButton>
          )}
          {auditLogFilterData?.fund_families.length && (
            <SelectButton
              title={t("common:fundFamily")}
              size="sm"
              isMulti={true}
              isFilterButton={true}
              value={filters.fund_family_ids}
              onChangeValue={(fund_family_ids) =>
                setFilters({ fund_family_ids })
              }
            >
              {selectOptionsFromGenericSelectData(
                auditLogFilterData.fund_families,
              )}
            </SelectButton>
          )}
          {hasFilters && (
            <Button variant="subtle" onClick={clearFilters}>
              {t("common:reset")}
            </Button>
          )}
        </HStack>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        {tableData && (
          <DataTable
            data={tableData}
            getLinkInfoForRow={getLinkInfoForRow}
            disablePinnedHeaderColumns={true}
            rowSelectionCheckboxes={false}
            onRowSelectionChange={noop}
            rowSelectionType="single"
            rowSelection={logEntryId ? { [logEntryId]: true } : undefined}
            sx={{
              ".ag-cell[col-id=time]": {
                textStyle: "bodyBold",
              },
            }}
          />
        )}
        {logEntryId &&
          tableData &&
          renderAuditingPanel({
            apiAuditLogEntryId: logEntryId,
            tableData,
            onClose: () => setLogEntryId(undefined),
          })}
      </TableBoxBody>
    </TableBox>
  );
};
